/**
 * @file useNavigationDraft.ts
 * @description Документ навигации части MENUS черновика вида сайта с
 * автосохранением (phase19.16, партия «SEO-техника», v0.19.65; SB146;
 * db-design-seo-tech §1.4): «Показывать хлебные крошки» — вкладка «Крошки»
 * панели «Весь сайт → Меню». Образец — `useCountersDraft`.
 *
 * Как у прочих частей (`useAutosavePart`): правка сразу в окне, запись —
 * через паузу, `expectedVersion` части MENUS (общая с пунктами меню —
 * `useSiteMenus` подхватывает новую версию из черновика), 409 — конфликт.
 * После записи каркас получает новую версию части и значение (`applyPart`):
 * правка попадает в ●N панели, «Что изменено» и «Отменить изменения»
 * (ответ «Отменить» — черновик целиком, значение подхватывается из него).
 * Сервер до v0.19.65 поля `navigation` не отдаёт — умолчания shared.
 */

import { readNavigationOptions, type SiteNavigationOptions } from "@chaiberi/shared";
import { useCallback, useMemo } from "react";

import { useAutosavePart, type AutosaveApi } from "./useAutosavePart";
import type { ApplyPart } from "./useThemeDraft";
import { siteView } from "../../lib/api-site-view";
import type { SiteViewDraft } from "../../lib/site-view/types";

export interface NavigationDraftApi extends AutosaveApi<SiteNavigationOptions> {
  navigation: SiteNavigationOptions;
  setBreadcrumbs: (on: boolean) => void;
}

/** Состояние документа навигации; `null` — черновик ещё не загружен. */
export function useNavigationDraftState(draft: SiteViewDraft | null, applyPart: ApplyPart): NavigationDraftApi | null {
  const loaded = draft !== null;
  const raw = draft?.navigation;
  const serverValue = useMemo(() => (loaded ? readNavigationOptions(raw) : null), [loaded, raw]);

  const api = useAutosavePart<SiteNavigationOptions>({
    serverValue,
    serverVersion: draft?.parts.MENUS?.version ?? 0,
    save: async (value, expectedVersion) => {
      const res = await siteView.saveNavigation({ expectedVersion, breadcrumbs: value.breadcrumbs });
      // Нет документа в ответе — оставить отправленное, а не умолчание.
      return { part: res.part, value: res.navigation ? readNavigationOptions(res.navigation) : value };
    },
    // После конфликта — черновик целиком: в нём и версия части MENUS, и документ навигации (одним запросом).
    load: async () => {
      const next = await siteView.draft();
      return { part: next.parts.MENUS, value: readNavigationOptions(next.navigation) };
    },
    onSaved: (result) => applyPart(result.part, { navigation: result.value }),
  });

  const navigation = api.value ?? serverValue;
  const { change } = api;
  const setBreadcrumbs = useCallback(
    (on: boolean) => {
      if (!navigation) return;
      change({ ...navigation, breadcrumbs: on }, ["breadcrumbs"]);
    },
    [change, navigation],
  );

  if (!draft || !navigation) return null;
  return { ...api, navigation, setBreadcrumbs };
}
